import { ComponentType, lazy } from "react"

type ComponentImport<T> = () => Promise<{ default: T }>

const RELOAD_KEY = "vite_chunk_reload"

/**
 * Registers a global handler for Vite preload errors.
 * This catches chunk load failures early (before React even tries to render)
 * and automatically reloads the page once to get fresh assets.
 *
 * This should be called once at app initialization.
 */
export function registerPreloadErrorHandler() {
  // Vite 4.2+ emits this event when a dynamic import fails
  window.addEventListener("vite:preloadError", (event) => {
    event.preventDefault()

    // Check if we already tried reloading
    const hasReloaded = sessionStorage.getItem(RELOAD_KEY)

    if (!hasReloaded) {
      // Mark that we're reloading to prevent infinite loop
      sessionStorage.setItem(RELOAD_KEY, "true")
      // Reload to get fresh assets
      window.location.reload()
    } else {
      // Already reloaded once, clear flag and let ErrorBoundary handle it
      sessionStorage.removeItem(RELOAD_KEY)
    }
  })

  // Clear the reload flag on successful page load
  // (means the reload worked and new assets loaded successfully)
  window.addEventListener("load", () => {
    sessionStorage.removeItem(RELOAD_KEY)
  })
}

/**
 * Wraps React.lazy with automatic retry logic for chunk load failures.
 * When a dynamically imported module fails to load (e.g., after deployment),
 * it will automatically reload the page once to get fresh assets.
 */
export function lazyWithRetry<T extends ComponentType<unknown>>(
  componentImport: ComponentImport<T>,
  chunkName?: string
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    const storageKey = `${RELOAD_KEY}_${chunkName || "default"}`

    try {
      const component = await componentImport()
      // Clear reload flag on successful load
      sessionStorage.removeItem(storageKey)
      return component
    } catch (error) {
      const isChunkLoadError =
        error instanceof Error &&
        (error.message.includes("dynamically imported module") ||
          error.message.includes("Loading chunk") ||
          error.message.includes("Failed to fetch"))

      // Check if we already tried reloading for this specific chunk
      const hasReloaded = sessionStorage.getItem(storageKey)

      if (isChunkLoadError && !hasReloaded) {
        // Mark that we're reloading this chunk
        sessionStorage.setItem(storageKey, "true")
        // Reload to get fresh assets
        window.location.reload()
        // Return a promise that never resolves (page is reloading)
        return new Promise(() => {})
      }

      // Already reloaded or different error - clear flag and throw to ErrorBoundary
      sessionStorage.removeItem(storageKey)
      throw error
    }
  })
}
